import React from 'react'
import { Switch, Route, RouteComponentProps, withRouter } from 'react-router'
import { NotFound } from '#src/components/Errors'
import { CartProvider } from '#src/containers/Cart'
import { PageKeyRoute, PageKeyPriority } from '@deal/page-key'

import ChatProvider from '#src/containers/Chat'
import CuratedRedirect from '#src/components/CuratedRedirect'
import * as Routes from './routes'

interface RouterProps extends RouteComponentProps<{}> {}

const Router: React.SFC<RouterProps> = () => {
  return (
    <>
      {/* Redirect from Deal.com to Curated.com */}
      <CuratedRedirect />
      <Switch>
        <Route path="/r" component={Routes.Redirects} />
        {/**
         * The authentication route requires special treament:
         *
         *   1) We don't want to initiate subscriptions that require authenticated state while
         *      logging out, or we'll hit race conditions between clearing authentication cookies
         *      and re-setting the subscription to no longer use the authenticated state.
         *
         *   2) The route should always be accessible, and not subject to the Deal.com => Curated.com
         *      redirect and splash screen.
         */}
        <PageKeyRoute
          path="/auth"
          pageKey="auth"
          priority={PageKeyPriority.TOP_LEVEL}
          component={Routes.Auth}
        />
        <PageKeyRoute
          path="/landing"
          pageKey="landing"
          priority={PageKeyPriority.TOP_LEVEL}
          component={Routes.Landing}
        />
        <PageKeyRoute
          path="/orders"
          pageKey="orders"
          priority={PageKeyPriority.TOP_LEVEL}
          component={Routes.Orders}
        />
        <PageKeyRoute
          path="/requests"
          pageKey="requests"
          priority={PageKeyPriority.TOP_LEVEL}
          component={Routes.Requests}
        />
        <PageKeyRoute
          path="/referral"
          pageKey="referral"
          priority={PageKeyPriority.TOP_LEVEL}
          component={Routes.Referral}
        />
        <Route
          path="/"
          render={() => (
            <CartProvider>
              <ChatProvider>
                <Switch>
                  <PageKeyRoute
                    path="/guest-checkout"
                    pageKey="guest-checkout"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.GuestCheckout}
                  />
                  <PageKeyRoute
                    path="/about-us"
                    pageKey="about-us"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.About}
                  />
                  <PageKeyRoute
                    path="/account"
                    pageKey="account"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Account}
                  />
                  <PageKeyRoute
                    path="/apply"
                    pageKey="apply"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Apply}
                  />
                  <PageKeyRoute
                    path="/careers"
                    pageKey="careers"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Careers}
                  />
                  <PageKeyRoute
                    path="/charters"
                    pageKey="charters-redirect"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.ChartersRedirect}
                  />
                  <PageKeyRoute
                    path="/c"
                    pageKey="categories"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Categories}
                  />
                  <PageKeyRoute
                    path="/reviews"
                    pageKey="reviews"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Reviews}
                  />
                  <PageKeyRoute
                    path="/curations"
                    pageKey="curations"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Curations}
                  />
                  <PageKeyRoute
                    path="/d"
                    pageKey="departments"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Departments}
                  />
                  <PageKeyRoute
                    path="/experiences"
                    pageKey="experiences"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Experiences}
                  />
                  <PageKeyRoute
                    path="/experts"
                    pageKey="experts"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Experts}
                  />
                  <PageKeyRoute
                    path="/feedback"
                    pageKey="feedback"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Feedback}
                  />
                  <PageKeyRoute
                    path="/faq"
                    pageKey="faq"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.FAQ}
                  />
                  <PageKeyRoute
                    path="/favorites"
                    pageKey="favorites"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Favorites}
                  />
                  <PageKeyRoute
                    path="/how-it-works"
                    pageKey="how-it-works"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.HowItWorks}
                  />
                  <PageKeyRoute
                    path="/in-action"
                    pageKey="in-action"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.InAction}
                  />
                  <PageKeyRoute
                    path="/inbox"
                    pageKey="inbox"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Inbox}
                  />
                  <PageKeyRoute
                    path="/itinerary"
                    pageKey="itinerary"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Itinerary}
                  />
                  <PageKeyRoute
                    path="/legal"
                    pageKey="legal"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Legal}
                  />
                  <PageKeyRoute
                    path="/meet-the-experts"
                    pageKey="meet-the-experts"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.MeetTheExperts}
                  />
                  <PageKeyRoute
                    path="/refer-a-friend"
                    pageKey="refer-a-friend"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.ReferAFriend}
                  />
                  <PageKeyRoute
                    path="/returns"
                    pageKey="returns"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Returns}
                  />
                  <PageKeyRoute
                    path="/products"
                    pageKey="products"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Products}
                  />
                  <PageKeyRoute
                    path="/push-notifications"
                    pageKey="push-notifications"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.PushNotifications}
                  />
                  <PageKeyRoute
                    path="/sellable"
                    pageKey="sellable"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Sellable}
                  />
                  <PageKeyRoute
                    path="/support"
                    pageKey="support"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Support}
                  />
                  <PageKeyRoute
                    path="/team"
                    pageKey="team"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Team}
                  />
                  <PageKeyRoute
                    path="/topics"
                    pageKey="topics"
                    priority={PageKeyPriority.TOP_LEVEL}
                    component={Routes.Topics}
                  />
                  {/* Root route */}
                  <PageKeyRoute
                    pageKey="home"
                    priority={PageKeyPriority.TOP_LEVEL}
                    path="/"
                    exact={true}
                    component={Routes.Home}
                  />
                  {/* 404 */}
                  <Route component={NotFound} />
                </Switch>
              </ChatProvider>
            </CartProvider>
          )}
        />
      </Switch>
    </>
  )
}

export default withRouter(Router)
